import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { selectOwnedAgentV2ChatListSidebarItems, selectAgentV2InitialLoadComplete, selectAgentV2ListLoading } from '@/agent-v2/conversations/store/selectors'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { kRouteSegments } from '@/routing/paths'
import type { ChatListSidebarItem } from '@/chat-common/conversation_list/schema'
import { kNewConversationTitle } from '@/chat-common/conversation_list/constants'

export type MatterChatHistoryNavItem = ChatListSidebarItem & {
  href: string
  /* Shown in place of the title until the chat's title is generated */
  titlePlaceholder: string | null
  /* A generated title is on its way to replace the placeholder */
  titlePending: boolean
}

export type UseMatterChatHistory = {
  items: MatterChatHistoryNavItem[]
  /** True until the initial fetch finishes, or while refetching with nothing cached yet. */
  isLoading: boolean
}

/**
 * Builds the side nav's Chat History list from the current user's chats in recency order.
 */
export function useMatterChatHistory(matterId: string | null, currentUserId: string | undefined): UseMatterChatHistory {
  const dispatch = useAppDispatch()

  const conversations = useAppSelector((state: RootState) => selectOwnedAgentV2ChatListSidebarItems(state, { matterId, currentUserId }))
  const sidebar = useAppSelector((state) => (matterId ? state.agentV2ConversationsState.sidebarListsByMatterId[matterId] : undefined))
  const v2Loading = useAppSelector((state: RootState) => selectAgentV2ListLoading(state, { matterId }))
  const v2InitialLoadComplete = useAppSelector((state: RootState) => selectAgentV2InitialLoadComplete(state, { matterId }))
  const titlePlaceholderByConversationId = useAppSelector((state: RootState) => state.agentV2ConversationsState.titlePlaceholderByConversationId)

  useEffect(() => {
    if (matterId) {
      dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId, forceRefresh: false, sidebar: true }))
    }
  }, [matterId, dispatch])

  const items = useMemo(
    () =>
      conversations.map((conversation): MatterChatHistoryNavItem => {
        const placeholder = conversation.title === kNewConversationTitle ? titlePlaceholderByConversationId[conversation.id] : undefined
        return {
          ...conversation,
          href: matterId ? buildConversationPath(matterId, conversation.id, kRouteSegments.agentV2) : '',
          titlePlaceholder: placeholder?.text ?? null,
          titlePending: placeholder?.titleGenerating ?? false,
        }
      }),
    [conversations, matterId, titlePlaceholderByConversationId]
  )

  return {
    items,
    isLoading:
      !currentUserId ||
      (sidebar ? (!sidebar.loaded && !sidebar.error) || (sidebar.loading && items.length === 0) : !v2InitialLoadComplete || (v2Loading && items.length === 0)),
  }
}
